<script setup>
import { ref, watch, computed } from 'vue'
import { Tabs, Tab, Select, Button, Icon, Breadcrumb, Checkbox, Input, Upcoming, Textarea } from '@components'
import GeneralItem from './GeneralItem.vue'
import Fieldset from './Fieldset.vue'

// Settings store.
import { useSettings } from '@store/settings'
const $settings = useSettings()
const $options = prebook_admin.options


const updateGeneral = async () => {
    await $settings.saveSettings()
    Toast.success('Updated settings.')
}

const currencySymbolText = computed(() => {
    return $settings.payment.currency_position === 'before' ? `${$settings.payment.currency_symbol}100` : `100${$settings.payment.currency_symbol}`
})

// Options.
const currencies = computed(() => {
    let uniqueCurrencies = new Set()
    let options = []

    $options.countries?.forEach((country) => {
        if (!uniqueCurrencies.has(country.currency)) {
            options.push({
                label: country.currency + ' - ' + country.symbol,
                value: country.currency,
                tags: [country.name, country.code],
            })
            uniqueCurrencies.add(country.currency)
        }
    })

    return options
})

const availablePaymentMethods = computed(() => {
    return $settings.payment.methods && $settings.payment.methods.filter(function(method) {

        if (isLocked && method.id === 'stripe') {
            return false
        }
        return method.enabled 
    }).map(function(method) {
        return {
            label: method.label || method.name,
            value: method.id,
        };
    });
})

const togglePaymentMethod = (method) => {
    if ($settings.payment.methods.includes(method)) {

        // Return if only one payment method is available
        if ($settings.payment.methods.length === 1) {
            return Toast.error('At least one payment method is required.')
        }

        $settings.payment.methods = $settings.payment.methods.filter((item) => item !== method)
    } else {
        $settings.payment.methods.push(method)
    }
}

const changedCurrency = (value) => {
    $settings.payment.currency = value
    $settings.payment.currency_symbol = $options.countries.find((country) => country.currency === value).symbol
}

// Payment method.
const movePaymentMethod = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    const methods = $settings.payment.methods

    if (newIndex < 0 || newIndex >= methods.length) {
        return
    }

    const temp = methods[index]
    methods[index] = methods[newIndex]
    methods[newIndex] = temp

    $settings.payment.methods = methods
}


// Watch $settings.payment.methods.
watch(() => $settings.payment.methods, (methods) => {
    // Disabling all payment methods is not allowed.
    if (methods.filter((method) => method.enabled).length === 0) {
        methods[0].enabled = true
    }

    // Auto select the first payment method if none is selected or the selected one is disabled.
    if ($settings.payment.default_payment_method === null || !methods.find((method) => method.id === $settings.payment.default_payment_method)?.enabled) {
        $settings.payment.default_payment_method = methods.find((method) => method.enabled).id
    }

}, { deep: true })

// Stripe.
const isStripeTestMode = computed(() => {
    return $settings.payment.methods.find((method) => method.id === 'stripe')?.secret_key?.startsWith('sk_test')
})

const priceSeparators = ref([
    {
        label: 'Comma-Dot',
        value: 'Comma-Dot',
        hint: '15,000.00',
    },
    {
        label: 'Dot-Comma',
        value: 'Dot-Comma',
        hint: '15.000,00',
    },
    {
        label: 'Space-Dot',
        value: 'Space-Dot',
        hint: '15 000.00',
    },
    {
        label: 'Space-Comma',
        value: 'Space-Comma',
        hint: '15 000,00',
    }
])
// const decimalNumbers = ref(['0', '1', '2', '3', '4', '5'])

const updateDecimalNumbers = (type) => {
    if (type == 'increment' && $settings.payment.price_decimals < 5) {
        $settings.payment.price_decimals++
    }

    if (type == 'decrement' && $settings.payment.price_decimals > 0) {
        $settings.payment.price_decimals--
    }
}

const isLocked = prebook_admin.is_pro != 1
</script>



<template>
    <div class="md:max-w-[736px] m-auto mt-[-22px] mb-2">
        <!-- breadcrumb -->
        <Breadcrumb :items="[
            { icon: true, name: 'settings01', route: { name: 'settings' } },
            { name: `${$settings.labels.payment?.plural}`, route: { name: 'settings.payments' } }
        ]" />


        <Tabs default="currency" design="underline" :class="`${$settings.isSaving ? 'opacity-60 pointer-events-none' : ''}`">
            <Tab id="currency">Currency</Tab>
            <Tab id="payment_methods" v-if="$settings.addons.payment">{{ $settings.labels.payment?.plural }} Methods</Tab>

            <template #currency>
                <div class="md:w-[736px] flex flex-col gap-7">

                    <!-- Currency  -->
                    <Fieldset>
                        Currency
                        <template #field>
                            <Select search :create="false" @change="changedCurrency" :options="currencies" v-model="$settings.payment.currency" class="w-full"></Select>
                        </template>
                    </Fieldset>

                    <!-- Currency Symbol Position  -->
                    <Fieldset>
                        Currency Symbol Position
                        <template #field>
                            <Input type="text" class="my-1.5 w-full" v-model="currencySymbolText" readonly>
                            <template #after>
                                <Select class="w-24" :options="{ before: 'Left', after: 'Right' }" v-model="$settings.payment.currency_position" borderless></Select>
                            </template>
                            </Input>
                        </template>
                    </Fieldset>

                    <!-- Price Separator -->
                    <Fieldset>
                        Price Separator
                        <template #field>
                            <Select :options="priceSeparators" v-model="$settings.payment.price_separator" class="w-full"></Select>
                        </template>
                    </Fieldset>

                    <!-- Price Decimals -->
                    <Fieldset>
                        Number of Decimals
                        <template #field>
                            <Input type="number" v-model="$settings.payment.price_decimals" :min="0" :max="5" iconLeft="minus" iconRight="plus" @iconLeftClick="updateDecimalNumbers('decrement')" @iconRightClick="updateDecimalNumbers('increment')" inputClass="text-center !w-[calc(100%-64px)]" inputContainerClass="!px-3 !h-12" iconLeftClass="text-[#344054] hover:bg-[#F2F4F7] rounded-full p-1 !w-8 !h-auto cursor-pointer" iconRightClass="text-[#344054] hover:bg-[#F2F4F7] rounded-full p-1 !w-8 !h-auto cursor-pointer"></Input>
                        </template>
                    </Fieldset>

                </div>
            </template>

            <template #payment_methods>
                <div class="md:w-[736px] flex flex-col gap-7">

                    <!-- default payment methods  -->
                    <Fieldset>
                        Default {{ $settings.labels.payment?.singular }} Method
                        <template #field>
                            <Select :options="availablePaymentMethods" v-model="$settings.payment.default_payment_method" class="w-full"></Select>
                        </template>
                    </Fieldset>

                    <!-- available payment methods -->
                    <div class="flex flex-col items-start gap-5 w-full">
                        <strong>Available {{ $settings.labels.payment?.plural }} Methods</strong>
                        <div class="flex flex-col gap-2 w-full">
                            
                            <div v-for="(method, index) in $settings.payment.methods" :key="method.id" class="border relative border-gray-100 bg-gray-50 rounded">

                            <!-- badge 'Upcoming'  -->
                            <Upcoming v-if="method.id === 'stripe'" />

                                <!-- <Checkbox v-model="method.enabled" :value="methodKey">{{ $options.methods[ methodKey ] }}</Checkbox> -->
                                <div class="px-5 py-5 flex items-center justify-between">
                                    <span class="font-semibold text-base flex items-center justify-start gap-2">
                                        <Checkbox v-model="method.enabled" :value="method.id" @change="togglePaymentMethod(index)"></Checkbox>
                                        {{ method.name }}
                                    </span>
                                    <div class="flex items-center justify-end gap-1 child:cursor-pointer child:z-10 child-hover:opacity-100 child:opacity-50">
                                        <svg v-if="index > 0" @click="movePaymentMethod(index, 'up')" xmlns="http://www.w3.org/2000/svg" class="fill-current w-4 rotate-180" viewBox="0 0 16 16">
                                            <path fill-rule="evenodd" d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1" />
                                        </svg>
                                        <svg v-if="index < $settings.payment.methods.length - 1" @click="movePaymentMethod(index, 'down')" xmlns="http://www.w3.org/2000/svg" class="fill-current w-4" viewBox="0 0 16 16">
                                            <path fill-rule="evenodd" d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1" />
                                        </svg>
                                    </div>
                                </div>

                                <div v-if="method.enabled" class="m-2 md:ml-5 my-2 flex flex-col gap-5 font-normal">
                                    <!-- change label text  -->
                                    <Fieldset class="border-transparent ring-transparent w-full" style="padding: 0 !important;">
                                        Custom label
                                        <template #field>
                                            <Input type="text" v-model="method.label" class="w-full"></Input>
                                        </template>
                                    </Fieldset>

                                    <!-- change description  -->
                                    <Fieldset class="border-transparent ring-transparent w-full" style="padding: 0 !important;">
                                        Custom description
                                        <template #field>
                                            <Textarea type="text" v-model="method.description" class="w-full"></Textarea>
                                        </template>
                                    </Fieldset>

                                    <!-- If stripe  -->
                                    <Fieldset v-if="method.id === 'stripe'" class="border-transparent ring-transparent items-start w-full" style="padding: 0 !important;">
                                        <label class="flex items-center gap-2">Stripe Secret Key <div v-if="isStripeTestMode" class="text-xs text-red-500">
                                                (Test Mode)
                                            </div></label>
                                        <template #field>
                                            <Input type="text" v-model="method.secret_key" class="w-full"></Input>
                                        </template>
                                    </Fieldset>


                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Tabs>


        <!-- Footer actions -->
        <div class="flex items-center gap-10">
            <Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] mt-10 justify-between" @click.prevent="$router.push({ name: 'settings' })">
                <div class="w-[70%]">Back</div>
                <template #before>
                    <Icon name="arrowNarrowLeft" size="20px"></Icon>
                </template>

            </Button>
            <Button @click="updateGeneral()" size="xl" type="primary" radius="square" classButton="w-full mt-10 justify-between">
                <div class="w-[95%]">Update</div>
            </Button>
        </div>
    </div>
</template>
